import type { FC, ReactNode } from "react";
import { useState } from "react";
import type { SxProps, Theme } from "@mui/material";
import { ListItemButton, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import { useFormContext } from "react-hook-form";
import { ChevronRightIcon, TriangleAlertIcon } from "../../shared/icons";
import type { Section } from "../types";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { useDynamicFormState } from "../DynamicFormProvider";
import { traverseQuestionCard } from "../shared/TraverseQuestionCard";
import { isAnswerRequestEmpty } from "../shared/isAnswerRequestEmpty";
import type { SectionGroupWithSections } from "./mapSectionsToSectionGroupsWithSections";

type SectionNavigationProps = { sections: Section[]; currentSectionId: string };
export const SectionNavigation: FC<SectionNavigationProps> = ({ sections, currentSectionId }) => {
  const { navigate } = usePrivacyNavigate();

  return (
    <Navigation gap={0.5}>
      {sections.map(section => {
        const isCurrent = section.id === currentSectionId;
        return (
          <SectionItem
            warningIndicator={<TriangleAlertIcon size={16} color={theme => theme.palette.warning.main} />}
            onClick={() => navigate(`../${section.id}`)}
            typographySx={{ color: theme => (isCurrent ? theme.palette.text.primary : theme.palette.text.tertiary) }}
            key={section.id}
            section={section}
            isCurrent={isCurrent}
          />
        );
      })}
    </Navigation>
  );
};

type SectionGroupNavigationProps = { currentSectionId: string; sectionGroupsWithSections: SectionGroupWithSections[] };

const SECTION_GROUP_GAP = 2;

export const SectionGroupsNavigation: FC<SectionGroupNavigationProps> = ({
  currentSectionId,
  sectionGroupsWithSections,
}) => {
  return (
    <Navigation gap={SECTION_GROUP_GAP}>
      {sectionGroupsWithSections.map(group => (
        <SectionGroupItem key={group.id} currentSectionId={currentSectionId} sectionGroupWithSections={group} />
      ))}
    </Navigation>
  );
};

const SectionGroupItem: FC<{ currentSectionId: string; sectionGroupWithSections: SectionGroupWithSections }> = ({
  currentSectionId,
  sectionGroupWithSections,
}) => {
  const { sections } = sectionGroupWithSections;
  const [isExpanded, setIsExpanded] = useState(true);
  const { navigate } = usePrivacyNavigate();

  if (sections.length === 0)
    throw new Error(`A section group(${sectionGroupWithSections.id}) must have at least one section.`);

  if (sections.length === 1) {
    /** If a section group only has one section ignore the section group and treat it has a normal section item */
    const [section] = sections;
    return (
      <SectionItem
        warningIndicator={<TriangleAlertIcon size={14} color={theme => theme.palette.warning.main} />}
        typographySx={{ fontWeight: 600 }}
        onClick={() => navigate(`../${section.id}`)}
        key={section.id}
        section={sections[0]}
        isCurrent={section.id === currentSectionId}
      />
    );
  }

  return (
    <Stack>
      <SectionItem
        warningIndicator={<TriangleAlertIcon size={14} color={theme => theme.palette.warning.main} />}
        onClick={() => setIsExpanded(!isExpanded)}
        typographySx={{ fontWeight: 600 }}
        sectionGroupWithSections={sectionGroupWithSections}
        isExpanded={isExpanded}
        isCurrent={!isExpanded && sections.some(section => section.id === currentSectionId)}
        renderEndIcon={() => (
          <ChevronRightIcon rotate={isExpanded ? 90 : 0} color={theme => theme.palette.text.secondary} size={14} />
        )}
      />
      <Collapse in={isExpanded} timeout={0}>
        <Stack sx={{ gap: 0.5, mt: 0.5 }}>
          {sections.map(section => {
            const isCurrent = section.id === currentSectionId;
            return (
              <SectionItem
                warningIndicator={<TriangleAlertIcon size={16} color={theme => theme.palette.warning.main} />}
                onClick={() => navigate(`../${section.id}`)}
                typographySx={!isCurrent ? { color: theme => theme.palette.text.tertiary } : {}}
                itemSx={isCurrent ? { fontWeight: 400 } : {}}
                key={section.id}
                section={section}
                isCurrent={isCurrent}
              />
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
};

type SectionItemProps = {
  warningIndicator: ReactNode;
  isCurrent: boolean;
  onClick: () => void;
  renderEndIcon?: () => ReactNode;
  typographySx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
} & (
  | { section: Section; sectionGroupWithSections?: undefined; isExpanded?: undefined }
  | { section?: undefined; sectionGroupWithSections: SectionGroupWithSections; isExpanded: boolean }
);

const SectionItem: FC<SectionItemProps> = ({
  section,
  sectionGroupWithSections,
  isExpanded,
  warningIndicator,
  isCurrent,
  onClick,
  renderEndIcon,
  typographySx,
  itemSx,
}) => {
  const hasUnasweredMandatoryQuestions = useHasUnansweredMandatoryQuestions(
    section ? [section] : sectionGroupWithSections.sections
  );

  const showWarningIndicator = hasUnasweredMandatoryQuestions;

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        "px": 2,
        "py": 1,
        "borderRadius": 1,
        "minHeight": "32px",
        "gap": 1,
        "cursor": "pointer",
        "&:hover": { bgcolor: theme => theme.palette.grey["200"] },
        "&.Mui-selected": { bgcolor: theme => theme.palette.grey["300"] },
        "&:hover.Mui-selected": { bgcolor: theme => theme.palette.grey["300"] },
        ...itemSx,
      }}
      selected={isCurrent}
    >
      <Typography
        component="span"
        sx={{
          mr: "auto",
          lineHeight: 1.4,
          fontWeight: isCurrent ? 600 : "normal",
          fontSize: theme => theme.typography.pxToRem(14),
          ...typographySx,
        }}
      >
        {section ? section.name : sectionGroupWithSections.name}
      </Typography>
      <Stack sx={{ flexDirection: "row", gap: 1, alignItems: "center" }}>
        {(() => {
          if (sectionGroupWithSections && isExpanded) return null;

          return showWarningIndicator && warningIndicator;
        })()}
        {renderEndIcon ? renderEndIcon() : null}
      </Stack>
    </ListItemButton>
  );
};

const useHasUnansweredMandatoryQuestions = (sections: Section[]) => {
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { getValues } = useFormContext();
  const unansweredMandatoryQuestions: string[] = [];

  if (mandatoryQuestionErrorMessage) {
    sections.forEach(section =>
      section.questionCards.forEach(question =>
        traverseQuestionCard(question, {
          onVisit: ({ id, mandatory }) => {
            if (mandatory && isAnswerRequestEmpty(getValues()[id])) unansweredMandatoryQuestions.push(id);
          },
        })
      )
    );
  }

  return unansweredMandatoryQuestions.length > 0;
};

const Navigation = ({ children, gap }: { children: ReactNode; gap?: number }) => {
  return (
    <Stack component="nav" sx={{ py: 3, px: 1, gap: gap }}>
      {children}
    </Stack>
  );
};
